import { useState } from "react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

type SocialLink = {
  label?: string | null;
  url?: string | null;
  iconUrl?: string | null;
};

function normalizeSocialLinks(raw: SocialLink[] | null | undefined): Array<{ label: string; url: string; iconUrl?: string }> {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const links: Array<{ label: string; url: string; iconUrl?: string }> = [];
  for (const item of raw) {
    const label = String(item?.label || "").trim();
    const url = String(item?.url || "").trim();
    const iconUrl = String(item?.iconUrl || "").trim();
    if (!label || !/^https?:\/\//i.test(url)) continue;
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ label, url, ...(iconUrl ? { iconUrl } : {}) });
  }
  return links;
}

export function Footer() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const { data: seoCfg } = useQuery<any>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Legal / trust pages, managed at Admin → Content → Pages.
   *
   * Falls back to the three built-ins so the footer always carries a visible
   * legal footing: it is part of the E-E-A-T signal for a YMYL finance site, and
   * it gives every page an internal link to /about, /privacy-policy and /terms.
   */
  const { data: pagesCfg } = useQuery<{ items: Array<{ slug: string; title: string; showInFooter: boolean; sortOrder: number }> }>({
    queryKey: ["/api/pages"],
    queryFn: async () => {
      const res = await fetch("/api/pages");
      if (!res.ok) return { items: [] };
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const footerPages = (() => {
    const items = Array.isArray(pagesCfg?.items) ? pagesCfg!.items : [];
    const visible = items
      .filter((p) => p && p.slug && p.showInFooter)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    if (visible.length) return visible;
    return [
      { slug: "about", title: "About", showInFooter: true, sortOrder: 10 },
      { slug: "privacy-policy", title: "Privacy Policy", showInFooter: true, sortOrder: 20 },
      { slug: "terms", title: "Terms & Conditions", showInFooter: true, sortOrder: 30 },
    ];
  })();

  const siteName = (seoCfg?.siteName || "BullBuzz").toString();
  const footerAbout =
    (seoCfg?.footerAbout ||
      "The world's leading portal for forex traders. Honest reviews, real-time news, and professional tools for the global trading community.")
      .toString();
  const copyright = (seoCfg?.copyright || "").toString().trim();
  const socialLinks = normalizeSocialLinks(seoCfg?.socialLinks);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "footer" }),
      });
      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(true);
        setEmail("");
        toast({ title: "Subscribed!", description: "You'll receive our market updates." });
      } else {
        toast({ 
          title: data.message === "Already subscribed!" ? "Already subscribed" : "Error", 
          description: data.message,
          variant: data.message === "Already subscribed!" ? "default" : "destructive"
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to subscribe. Please try again.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-[#050914] pt-16 pb-8 text-slate-200">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <span className="font-heading font-bold text-2xl text-white">{siteName}</span>
            </div>
            <p className="text-sm text-slate-300 mb-6">
              {footerAbout}
            </p>
            {socialLinks.length ? (
              <div className="flex flex-wrap items-center gap-3" aria-label="Social links">
                {socialLinks.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow ${link.label}`}
                    title={link.label}
                    className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-xs font-black uppercase text-slate-100 transition-colors hover:border-primary/50 hover:bg-primary hover:text-black"
                  >
                    {link.iconUrl ? (
                      <img src={link.iconUrl} alt="" className="h-full w-full rounded-full object-contain p-2" loading="lazy" />
                    ) : (
                      link.label.slice(0, 2)
                    )}
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Brokers</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link href="/brokers" className="hover:text-primary transition-colors">Top Rated Brokers</Link></li>
              <li><Link href="/brokers" className="hover:text-primary transition-colors">ECN Brokers</Link></li>
              <li><Link href="/brokers" className="hover:text-primary transition-colors">Crypto Brokers</Link></li>
              <li><Link href="/brokers" className="hover:text-primary transition-colors">No Deposit Bonus</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Tools & Services</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link href="/vps" className="hover:text-primary transition-colors">Forex VPS</Link></li>
              <li><Link href="/eas" className="hover:text-primary transition-colors">EA Listing</Link></li>
              <li><Link href="/calculators" className="hover:text-primary transition-colors">Trading Calculators</Link></li>
              <li><Link href="/education" className="hover:text-primary transition-colors">Education Center</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Newsletter</h3>
            <p className="text-sm text-slate-300 mb-4">Subscribe for daily market updates.</p>
            {isSubscribed ? (
              <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-3 rounded-lg border border-green-500/30">
                <Check className="w-5 h-5" />
                <span className="text-sm font-medium">Subscribed!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address" 
                  className="bg-[#0b1220] border border-white/10 border-r-0 text-white px-4 py-2 rounded-l-md w-full focus:ring-1 focus:ring-primary outline-none"
                  data-testid="input-newsletter-email"
                  required
                />
                <button 
                  type="submit"
                  disabled={isLoading}
                  className="bg-primary text-primary-foreground px-4 py-2 rounded-r-md font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                  data-testid="button-newsletter-subscribe"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Keep this lighter copy readable on the deep footer background. */}
        <div className="border-t border-white/10 pt-8 mt-8 text-center text-xs text-slate-300">
          {footerPages.length ? (
            <nav aria-label="Legal and company pages" className="mb-6">
              <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                {footerPages.map((page) => (
                  <li key={page.slug}>
                    <Link
                      href={`/${page.slug}`}
                      className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-300 transition-colors hover:text-primary"
                    >
                      {page.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
          <p className="mb-4">
            Risk Warning: Trading Forex and CFDs carries a high level of risk to your capital and you should only trade with money you can afford to lose.
            The content on this website is for informational purposes only and does not constitute financial advice.
          </p>
          <p>
            {copyright ? copyright : `© ${new Date().getFullYear()} ${siteName}. All rights reserved.`}
          </p>
        </div>
      </div>
    </footer>
  );
}
